import { useEffect, useState } from "react";
import { Plus, ArrowLeft, Save, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Field, Area, Select } from "@/components/admin/ui";
import RichTextEditor from "@/components/editor/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";
import { ContentTable, csv, arr } from "./Blog";

const BLANK = { title: "", slug: "", description: "", featured_image: "", categories: [], tags: [], author: "Healthy Roots", status: "draft", prep_time: "", cook_time: "", servings: "", ingredients: [], instructions: [], content: "", seo: {} };

export default function Recipes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [picker, setPicker] = useState(false);
  const load = () => api.get("/admin/recipes").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, categories: csv(editing.categories), tags: csv(editing.tags),
      ingredients: lines(editing.ingredients), instructions: lines(editing.instructions) };
    try {
      if (editing.id) await api.put(`/admin/recipes/${editing.id}`, body);
      else await api.post("/admin/recipes", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this recipe?")) return; await api.delete(`/admin/recipes/${id}`); toast.success("Deleted"); load(); };
  const dup = async (id) => { await api.post(`/admin/recipes/${id}/duplicate`); toast.success("Duplicated"); load(); };

  if (editing) return (
    <div data-testid="recipe-editor">
      <PageHeader title={editing.id ? "Edit Recipe" : "New Recipe"}>
        <Btn variant="outline" onClick={() => setEditing(null)}><ArrowLeft size={16} /> Back</Btn>
        <Btn onClick={save} data-testid="save-recipe-btn"><Save size={16} /> Save</Btn>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4">
          <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <Area label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} rows={2} />
          <Area label="Ingredients (one per line)" value={txt(editing.ingredients)} onChange={(v) => setEditing({ ...editing, ingredients: v })} rows={6} />
          <Area label="Instructions (one step per line)" value={txt(editing.instructions)} onChange={(v) => setEditing({ ...editing, instructions: v })} rows={6} />
          <div><span className="text-sm font-medium text-emerald-900">Additional notes (optional)</span><div className="mt-1"><RichTextEditor value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} /></div></div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 h-fit">
          <Select label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
          <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
          <div><span className="text-sm font-medium text-emerald-900">Featured image</span>
            <div className="flex gap-2 mt-1"><input value={editing.featured_image || ""} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none" /><button onClick={() => setPicker(true)} className="rounded-lg border border-stone-300 px-3 py-2 hover:bg-stone-100"><ImageIcon size={15} /></button></div>
            {editing.featured_image && <img src={editing.featured_image} alt="" className="mt-2 h-24 w-full rounded-lg object-cover border" />}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Prep" value={editing.prep_time} onChange={(v) => setEditing({ ...editing, prep_time: v })} />
            <Field label="Cook" value={editing.cook_time} onChange={(v) => setEditing({ ...editing, cook_time: v })} />
            <Field label="Serves" value={editing.servings} onChange={(v) => setEditing({ ...editing, servings: v })} />
          </div>
          <Field label="Categories (comma separated)" value={arr(editing.categories)} onChange={(v) => setEditing({ ...editing, categories: v })} />
        </div>
      </div>
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(url) => setEditing({ ...editing, featured_image: url })} />
    </div>
  );

  return (
    <div data-testid="admin-recipes">
      <PageHeader title="Recipes" subtitle="Publish recipes with ingredients and steps">
        <Btn onClick={() => setEditing({ ...BLANK })} data-testid="new-recipe-btn"><Plus size={16} /> New Recipe</Btn>
      </PageHeader>
      <ContentTable items={items} onEdit={setEditing} onDel={del} onDup={dup} prefix="recipes" />
    </div>
  );
}

const lines = (v) => Array.isArray(v) ? v : (v || "").split("\n").map((s) => s.trim()).filter(Boolean);
const txt = (v) => Array.isArray(v) ? v.join("\n") : (v || "");
